import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Loader2, ArrowRight, Mail, Volume2 } from 'lucide-react';
import { LOGO_URL, LOGO_URL_DARK } from '../components/onboarding/constants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_SUCCESS_TEXT = {
  show_eyebrow: true,
  eyebrow: 'Final whistle',
  heading_line1: 'Welcome to the team,',
  heading_line2: 'Coach {name}!',
  subtitle_with_team: 'You and {team} are all set up. Time to start coaching.',
  subtitle_no_team: "You're all set up. Time to start coaching.",
  cta_button: 'Enter the Clubhouse',
  email_hint: "We've sent a confirmation to your email.",
  processing_heading: 'Processing payment...',
  processing_subtitle: 'Please wait while we confirm your subscription.',
  error_heading: 'Something went wrong',
  error_subtitle: "We couldn't confirm your payment. Please contact support.",
  error_retry_button: 'Try Again',
  plan_chip_suffix: 'active',
  plan_label_monthly: 'Monthly Pro · €8.99/mo',
  plan_label_annual: 'Annual Pro · €1.99 trial',
  plan_label_lifetime: 'Lifetime Deal · €59.99',
};

const fillTemplate = (str, vars) =>
  Object.entries(vars).reduce((s, [k, v]) => s.replaceAll(`{${k}}`, v ?? ''), str || '');

const CONFETTI_COLORS = [
  '#007a33', // brand green
  '#FFFFFF', // white
  '#00994d', // brand green light
  '#F8FAFC', // off-white
  '#005c26', // brand green dark
  '#FFFFFF', // white (extra)
];

/**
 * Build a deterministic-ish set of confetti pieces. Generated once on success
 * so re-renders don't restart the animation.
 */
function buildConfetti(count = 36) {
  return Array.from({ length: count }, (_, i) => {
    const left = Math.random() * 100;
    const drift = (Math.random() - 0.5) * 200; // px horizontal drift
    const dur = 2.6 + Math.random() * 2.4;     // 2.6 – 5.0 s
    const delay = 1.2 + Math.random() * 1.8;   // 1.2 – 3.0 s (after the goal moment)
    const color = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
    const tilt = Math.random() * 360;
    const w = 6 + Math.random() * 6;
    const h = 10 + Math.random() * 10;
    return { left, drift, dur, delay, color, tilt, w, h };
  });
}

/**
 * Play the success-page crowd cheer. Strategy:
 *   1. If `audioUrl` is provided (admin custom OR shipped default),
 *      try to play it via <audio>. Browsers honour the user-gesture
 *      already in flight (the speaker click).
 *   2. If that fails (network error, codec mismatch), fall back to
 *      a Web Audio synth so the click is never silent.
 *
 * Returns a Promise that resolves once playback starts. We don't await
 * playback to finish — the speaker button keeps state-free.
 */
function playSynthCheer() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return false;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;
    const duration = 1.1;

    // Pink-ish noise buffer
    const bufferSize = Math.floor(ctx.sampleRate * duration);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let b0 = 0, b1 = 0, b2 = 0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99765 * b0 + white * 0.0990460;
      b1 = 0.96300 * b1 + white * 0.2965164;
      b2 = 0.57000 * b2 + white * 1.0526913;
      data[i] = (b0 + b1 + b2 + white * 0.1848) * 0.15;
    }
    const noiseSrc = ctx.createBufferSource();
    noiseSrc.buffer = buffer;
    const bandpass = ctx.createBiquadFilter();
    bandpass.type = 'bandpass';
    bandpass.Q.value = 1.2;
    bandpass.frequency.setValueAtTime(700, now);
    bandpass.frequency.linearRampToValueAtTime(1400, now + 0.45);
    bandpass.frequency.linearRampToValueAtTime(1100, now + duration);
    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.0001, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.55, now + 0.18);
    noiseGain.gain.exponentialRampToValueAtTime(0.40, now + 0.55);
    noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    noiseSrc.connect(bandpass).connect(noiseGain);

    const voice1 = ctx.createOscillator();
    voice1.type = 'sine';
    voice1.frequency.setValueAtTime(440, now);
    voice1.frequency.exponentialRampToValueAtTime(660, now + 0.4);
    voice1.frequency.exponentialRampToValueAtTime(523, now + duration);
    const voiceGain1 = ctx.createGain();
    voiceGain1.gain.setValueAtTime(0.0001, now);
    voiceGain1.gain.exponentialRampToValueAtTime(0.08, now + 0.2);
    voiceGain1.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    voice1.connect(voiceGain1);

    const voice2 = ctx.createOscillator();
    voice2.type = 'sine';
    voice2.detune.value = 18;
    voice2.frequency.setValueAtTime(587, now);
    voice2.frequency.exponentialRampToValueAtTime(784, now + 0.45);
    voice2.frequency.exponentialRampToValueAtTime(659, now + duration);
    const voiceGain2 = ctx.createGain();
    voiceGain2.gain.setValueAtTime(0.0001, now);
    voiceGain2.gain.exponentialRampToValueAtTime(0.06, now + 0.25);
    voiceGain2.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    voice2.connect(voiceGain2);

    const master = ctx.createGain();
    master.gain.value = 0.85;
    noiseGain.connect(master);
    voiceGain1.connect(master);
    voiceGain2.connect(master);
    master.connect(ctx.destination);

    noiseSrc.start(now);
    voice1.start(now);
    voice2.start(now);
    noiseSrc.stop(now + duration);
    voice1.stop(now + duration);
    voice2.stop(now + duration);
    noiseSrc.onended = () => { try { ctx.close(); } catch (_) { /* noop */ } };
    return true;
  } catch (_) {
    return false;
  }
}

function playCrowdCheer(audioUrl) {
  // Prefer the real recorded file. If the admin pasted a custom URL it lives
  // in `audioUrl`; otherwise fall back to the shipped default at /sounds/crowd-cheer.wav.
  const src = (audioUrl && audioUrl.trim()) || '/sounds/crowd-cheer.wav';
  try {
    const audio = new Audio(src);
    audio.preload = 'auto';
    audio.volume = 0.75;
    const promise = audio.play();
    if (promise && typeof promise.catch === 'function') {
      // If the file refuses to play (404, autoplay block on rare browsers,
      // codec mismatch on a custom URL), fall back to synthesized cheer.
      promise.catch(() => { playSynthCheer(); });
    }
    return true;
  } catch (_) {
    return playSynthCheer();
  }
}

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const { user, fetchUser } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking | success | error
  const [paidPlan, setPaidPlan] = useState(null);
  const [successText, setSuccessText] = useState(DEFAULT_SUCCESS_TEXT);
  const sessionId = searchParams.get('session_id');
  const previewMode = searchParams.get('preview'); // ?preview=success for QA / design preview

  // Theme — URL param wins (so direct preview links work), then localStorage, then dark.
  const themeParam = searchParams.get('theme');
  const theme = themeParam === 'light' || themeParam === 'dark'
    ? themeParam
    : (localStorage.getItem('landingTheme') || 'dark');
  const isLight = theme === 'light';

  // Confetti is generated once when status becomes "success"
  const confetti = useMemo(() => (status === 'success' ? buildConfetti(36) : []), [status]);

  // Fetch CMS-editable text (admin can override every label on this page).
  useEffect(() => {
    axios.get(`${API}/onboarding-config`).then(r => {
      if (r.data?.success_page) {
        setSuccessText(prev => ({ ...DEFAULT_SUCCESS_TEXT, ...r.data.success_page }));
      }
    }).catch(() => {});

    // Live pricing overlay — keeps plan chips in sync with /api/pricing
    axios.get(`${API}/pricing`).then(r => {
      const plans = r.data?.plans || {};
      const fmt = (n) => `\u20AC${(Number(n) || 0).toFixed(2)}`;
      const m = plans.monthly, a = plans.annual, l = plans.lifetime;
      const overlays = {};
      if (m) overlays.plan_label_monthly = `${m.label || 'Monthly'} Pro \u00b7 ${fmt(m.effective_amount ?? m.base_amount)}/mo`;
      if (a) overlays.plan_label_annual = a.trial_amount != null
        ? `${a.label || 'Annual'} Pro \u00b7 ${fmt(a.effective_amount ?? a.trial_amount)} trial`
        : `${a.label || 'Annual'} Pro \u00b7 ${fmt(a.effective_amount ?? a.base_amount)}/yr`;
      if (l) overlays.plan_label_lifetime = `${l.label || 'Lifetime Deal'} \u00b7 ${fmt(l.effective_amount ?? l.base_amount)}`;
      // Only overlay defaults — never override admin-customised text from onboarding-config.
      setSuccessText(prev => {
        const out = { ...prev };
        for (const k of Object.keys(overlays)) {
          if (prev[k] === DEFAULT_SUCCESS_TEXT[k]) out[k] = overlays[k];
        }
        return out;
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    // QA / design preview: show the success state without a real Stripe session
    if (previewMode === 'success') {
      setStatus('success');
      setPaidPlan(searchParams.get('plan') || 'annual');
      return;
    }
    if (!sessionId) {
      setStatus('error');
      return;
    }
    pollStatus(sessionId, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, previewMode]);

  const pollStatus = async (sid, attempt) => {
    if (attempt >= 8) {
      setStatus('error');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/stripe/checkout-status/${sid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.payment_status === 'paid') {
        setPaidPlan(res.data.plan_id || res.data.subscription_plan || null);
        setStatus('success');
        await fetchUser(); // Refresh user data with new subscription
      } else if (res.data.status === 'expired') {
        setStatus('error');
      } else {
        setTimeout(() => pollStatus(sid, attempt + 1), 2000);
      }
    } catch {
      setTimeout(() => pollStatus(sid, attempt + 1), 2000);
    }
  };

  // Coach name + team name for the welcome message
  const coachName = (user?.display_name || user?.name || '').split(' ')[0] || 'Coach';
  const teamName = user?.team_name || '';
  const planKey = paidPlan || user?.subscription_plan || user?.plan;
  const planLabelMap = {
    monthly: successText.plan_label_monthly,
    annual: successText.plan_label_annual,
    lifetime: successText.plan_label_lifetime,
  };
  const planLabel = planLabelMap[planKey] || 'Premium Access';

  // Resolve all editable strings with placeholder substitution
  const vars = { name: coachName, team: teamName };
  const headingLine1 = fillTemplate(successText.heading_line1, vars);
  const headingLine2 = fillTemplate(successText.heading_line2, vars);
  const subtitle = teamName
    ? fillTemplate(successText.subtitle_with_team, vars)
    : fillTemplate(successText.subtitle_no_team, vars);

  return (
    <div
      className={`auth-container relative overflow-hidden ${isLight ? 'bg-white' : 'bg-gray-900'}`}
      data-theme={theme}
      style={{
        background: isLight
          ? 'radial-gradient(ellipse at 50% 0%, rgba(0,122,51,0.10) 0%, transparent 70%), #FFFFFF'
          : 'radial-gradient(ellipse at 50% 0%, rgba(0,122,51,0.30) 0%, transparent 70%), #111827',
      }}
      data-testid="payment-success-page"
    >
      {/* Stadium-floor glow behind everything — uses brand green to match landing */}
      {status === 'success' && (
        <div
          className="absolute left-1/2 top-1/3 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full blur-3xl pointer-events-none animate-psGlowPulse"
          style={{
            background: isLight
              ? 'radial-gradient(circle, rgba(0,122,51,0.18), transparent 60%)'
              : 'radial-gradient(circle, rgba(0,122,51,0.45), transparent 60%)',
          }}
          aria-hidden
        />
      )}

      {/* Confetti layer */}
      {status === 'success' && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden>
          {confetti.map((c, i) => (
            <span
              key={i}
              className="ps-confetti"
              style={{
                left: `${c.left}%`,
                width: `${c.w}px`,
                height: `${c.h}px`,
                background: c.color,
                transform: `rotate(${c.tilt}deg)`,
                '--ps-drift': `${c.drift}px`,
                '--ps-dur': `${c.dur}s`,
                '--ps-delay': `${c.delay}s`,
                // White confetti on a light bg gets a faint brand outline so it stays visible
                boxShadow: isLight && (c.color === '#FFFFFF' || c.color === '#F8FAFC')
                  ? '0 0 0 1px rgba(0,122,51,0.35)'
                  : 'none',
              }}
            />
          ))}
        </div>
      )}

      <div className="relative w-full max-w-md px-4 z-10">
        {/* CHECKING ─────────────────────────────────────────── */}
        {status === 'checking' && (
          <div className="text-center animate-fadeIn">
            <Loader2 className={`w-16 h-16 mx-auto mb-4 animate-spin ${isLight ? 'text-[#007a33]' : 'text-white'}`} />
            <h1 className={`text-2xl font-bold ${isLight ? 'text-gray-900' : 'text-white'}`}>{successText.processing_heading}</h1>
            <p className={`mt-2 ${isLight ? 'text-gray-600' : 'text-gray-400'}`}>{successText.processing_subtitle}</p>
          </div>
        )}

        {/* ERROR ────────────────────────────────────────────── */}
        {status === 'error' && (
          <div className="text-center animate-fadeIn">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${isLight ? 'bg-red-100' : 'bg-red-500/20'}`}>
              <span className={`text-4xl ${isLight ? 'text-red-600' : 'text-red-400'}`}>!</span>
            </div>
            <h1 className={`text-2xl font-bold ${isLight ? 'text-gray-900' : 'text-white'}`}>{successText.error_heading}</h1>
            <p className={`mt-2 ${isLight ? 'text-gray-600' : 'text-gray-400'}`}>{successText.error_subtitle}</p>
            <button
              onClick={() => navigate('/onboarding?step=plan')}
              className={`mt-4 px-6 py-2 rounded-xl font-medium transition-colors ${
                isLight
                  ? 'bg-[#007a33] text-white hover:bg-[#005c26]'
                  : 'bg-white text-gray-900 hover:bg-gray-100'
              }`}
              data-testid="payment-error-retry-btn"
            >
              {successText.error_retry_button}
            </button>
          </div>
        )}

        {/* SUCCESS ──────────────────────────────────────────── */}
        {status === 'success' && (
          <div className="text-center">
            {/* FT-style match tracker — same look as onboarding, ball settles in goal */}
            <SuccessTracker
              isLight={isLight}
              cheerEnabled={successText.cheer_sound_enabled !== false}
              cheerSoundUrl={successText.cheer_sound_url}
            />

            {/* Welcome card rises up after the goal moment */}
            <div className="animate-psCardRiseUp mt-8">
              {successText.show_eyebrow !== false && successText.eyebrow && (
                <p className={`text-xs font-bold tracking-[0.3em] uppercase mb-2 ${isLight ? 'text-[#007a33]' : 'text-[#00994d]'}`}>
                  {successText.eyebrow}
                </p>
              )}
              <h1
                className={`text-3xl sm:text-4xl font-black leading-tight ${isLight ? 'text-gray-900' : 'text-white'}`}
                data-testid="payment-success-heading"
              >
                {headingLine1}<br />
                <span style={{ color: isLight ? '#007a33' : '#00994d' }}>{headingLine2}</span>
              </h1>
              <p className={`mt-3 text-sm sm:text-base leading-relaxed ${isLight ? 'text-gray-600' : 'text-gray-400'}`}>
                {subtitle}
              </p>

              {/* Plan chip */}
              <div
                className={`inline-flex items-center gap-2 mt-5 px-4 py-1.5 rounded-full ${
                  isLight
                    ? 'bg-[#007a33]/10 border border-[#007a33]/20'
                    : 'bg-[#007a33]/20 border border-[#007a33]/30'
                }`}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full animate-pulse"
                  style={{ background: isLight ? '#007a33' : '#00994d' }}
                />
                <span
                  className="text-xs font-semibold tracking-wide"
                  style={{ color: isLight ? '#007a33' : '#00994d' }}
                >
                  {planLabel} {successText.plan_chip_suffix}
                </span>
              </div>

              {/* CTA — uses brand green to match landing buttons */}
              <button
                onClick={() => navigate('/clubhouse', { replace: true })}
                className="mt-7 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-2xl bg-[#007a33] hover:bg-[#005c26] active:bg-[#004d20] text-white font-bold text-base transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{ boxShadow: '0 10px 24px rgba(0,122,51,0.35)' }}
                data-testid="enter-clubhouse-btn"
              >
                {successText.cta_button}
                <ArrowRight className="w-5 h-5" />
              </button>

              {/* Email confirmation hint */}
              <p className={`mt-5 inline-flex items-center gap-1.5 text-xs ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>
                <Mail className="w-3.5 h-3.5" />
                {successText.email_hint}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Goal Scene Component ──────────────────────────────────────
/**
 * Match-themed FT (full-time) tracker — mirrors the onboarding header progress bar
 * (KO → HT → FT track with rolling football, white goal at the right end).
 * On mount, the ball bounces into the back of the net and settles inside the goal,
 * marking the coach's "final whistle" moment.
 */
function SuccessTracker({ isLight, cheerEnabled = true, cheerSoundUrl = '' }) {
  const stroke = isLight ? '#0F172A' : '#FFFFFF';
  // Light theme: render the inner net lines as a soft slate so they don't
  // look like dark stripes cutting through the ball. Dark theme keeps white
  // at 0.4 opacity which already reads as a subtle net.
  const netStroke = isLight ? '#94A3B8' : '#FFFFFF';
  const netOpacity = isLight ? 0.28 : 0.4;
  const trackDimClass = isLight ? 'bg-gray-300' : 'bg-white/20';
  const progressFillClass = isLight ? 'bg-[#007a33]' : 'bg-emerald-500';
  const labelActiveClass = isLight ? 'text-gray-900' : 'text-white';
  const matchLabels = ['KO', 'HT', 'FT'];
  const logoSrc = isLight ? LOGO_URL : LOGO_URL_DARK;
  // Light-theme: stronger drop shadow gives the ball weight against the
  // pale background. Dark-theme: subtle glow only.
  const ballShadow = isLight
    ? 'drop-shadow(0 2px 3px rgba(15, 23, 42, 0.25))'
    : 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.4))';

  return (
    <div className="flex flex-col items-center justify-center" data-testid="success-tracker">
      {/* Logo — matches the onboarding pages, spins rapidly on the FT moment */}
      <div className="text-center mb-6 animate-fadeIn">
        <img
          src={logoSrc}
          alt="Logo"
          className="w-20 h-20 mx-auto mb-3 object-contain animate-psLogoCelebrate"
          data-testid="success-tracker-logo"
        />
      </div>
      <div className="relative w-full max-w-[260px] mx-auto">
        {/* Track row */}
        <div className="relative h-10">
          {/* Background line */}
          <div
            className={`absolute top-1/2 h-px -translate-y-1/2 rounded-full ${trackDimClass}`}
            style={{ left: '18px', right: '18px' }}
          />
          {/* Progress fill — animates in from 0 → 100% on mount */}
          <div
            className={`absolute top-1/2 h-[2px] -translate-y-1/2 rounded-full ${progressFillClass} animate-psProgressFill`}
            style={{ left: '18px' }}
          />
          {/* KO / HT / FT stops — all filled (FT state) */}
          {matchLabels.map((_, i) => {
            const stopPercent = (i / (matchLabels.length - 1)) * 100;
            return (
              <div
                key={i}
                className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full w-2 h-2 ${progressFillClass}`}
                style={{
                  left: `calc(18px + (100% - 36px) * ${stopPercent / 100})`,
                  animation: `psStopPop 0.5s ease-out ${0.5 + i * 0.35}s both`,
                }}
                data-testid={`success-tracker-stop-${i + 1}`}
              />
            );
          })}

          {/* Football — bounces into the back of the net. z-20 keeps it on
              top of BOTH the net (z-5) and the goal frame (z-10), so dark
              net/post strokes never slice through the ball in light mode. */}
          <div
            className="absolute top-1/2 z-20 animate-psBallBounceIntoGoal"
            style={{ left: '18px', transform: 'translate(-50%, -50%)', filter: ballShadow }}
            data-testid="success-tracker-ball"
          >
            <svg width="22" height="22" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <radialGradient id="psSuccessBallShade" cx="35%" cy="30%" r="70%">
                  <stop offset="0%" stopColor="#FFFFFF" />
                  <stop offset="78%" stopColor="#F1F5F9" />
                  <stop offset="100%" stopColor="#CBD5E1" />
                </radialGradient>
              </defs>
              <circle cx="24" cy="24" r="20" fill="url(#psSuccessBallShade)" stroke="#0F172A" strokeWidth="2" />
              <polygon points="24,14 32,19.5 29,28 19,28 16,19.5" fill="#0F172A" />
              <path d="M24 14 L24 6" stroke="#0F172A" strokeWidth="1.25" />
              <path d="M32 19.5 L39 17.5" stroke="#0F172A" strokeWidth="1.25" />
              <path d="M29 28 L34 33" stroke="#0F172A" strokeWidth="1.25" />
              <path d="M19 28 L14 33" stroke="#0F172A" strokeWidth="1.25" />
              <path d="M16 19.5 L9 17.5" stroke="#0F172A" strokeWidth="1.25" />
            </svg>
          </div>

          {/* Goal NET — rendered BEHIND the ball at z-5 so net strands never
              cut through the ball at the settle position. */}
          <div
            className="absolute top-1/2 right-0 -translate-y-1/2 z-[5] animate-psGoalShake"
            data-testid="success-tracker-net"
            aria-hidden
          >
            <svg width="36" height="22" viewBox="0 0 64 48" xmlns="http://www.w3.org/2000/svg">
              <g className="animate-psNetRipple">
                <line x1="18" y1="12" x2="18" y2="40" stroke={netStroke} strokeWidth="0.75" opacity={netOpacity} />
                <line x1="26" y1="12" x2="26" y2="40" stroke={netStroke} strokeWidth="0.75" opacity={netOpacity} />
                <line x1="32" y1="12" x2="32" y2="40" stroke={netStroke} strokeWidth="0.75" opacity={netOpacity} />
                <line x1="38" y1="12" x2="38" y2="40" stroke={netStroke} strokeWidth="0.75" opacity={netOpacity} />
                <line x1="46" y1="12" x2="46" y2="40" stroke={netStroke} strokeWidth="0.75" opacity={netOpacity} />
                <line x1="10" y1="22" x2="54" y2="22" stroke={netStroke} strokeWidth="0.75" opacity={netOpacity} />
                <line x1="10" y1="32" x2="54" y2="32" stroke={netStroke} strokeWidth="0.75" opacity={netOpacity} />
              </g>
            </svg>
          </div>

          {/* Goal FRAME (posts + crossbar) — sits BEHIND the ball (z-10) but
              ABOVE the net (z-5). This way the frame still outlines the goal
              cleanly while the ball reads as a solid object on top. */}
          <div
            className="absolute top-1/2 right-0 -translate-y-1/2 z-10 animate-psGoalShake"
            data-testid="success-tracker-goal"
          >
            <svg width="36" height="22" viewBox="0 0 64 48" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 12 L10 40" stroke={stroke} strokeWidth="2.5" strokeLinecap="round" />
              <path d="M54 12 L54 40" stroke={stroke} strokeWidth="2.5" strokeLinecap="round" />
              <path d="M10 12 L54 12" stroke={stroke} strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </div>

          {/* GOAL! impact burst — two brand-green ring pulses + "GOAL!" badge,
              fires in sync with the ball's squash-bounce window (~1.5s).
              Pointer-events disabled so it never blocks interaction. Honours
              prefers-reduced-motion via App.css. */}
          <div
            className="absolute top-1/2 -translate-y-1/2 pointer-events-none"
            style={{ right: '8px', width: '24px', height: '24px', zIndex: 25 }}
            data-testid="success-tracker-burst"
            aria-hidden
          >
            <span className="ps-burst-ring ring-1" />
            <span className="ps-burst-ring ring-2" />
            <span className="ps-burst-label" data-testid="success-tracker-burst-label">GOAL!</span>
          </div>
        </div>

        {/* KO / HT / FT labels */}
        <div className="relative mt-1">
          {matchLabels.map((label, i) => {
            const stopPercent = (i / (matchLabels.length - 1)) * 100;
            return (
              <span
                key={i}
                className={`absolute top-0 -translate-x-1/2 text-[10px] font-semibold tracking-wider ${labelActiveClass}`}
                style={{ left: `calc(18px + (100% - 36px) * ${stopPercent / 100})` }}
                data-testid={`success-tracker-label-${i + 1}`}
              >
                {label}
              </span>
            );
          })}
          <span className="invisible text-[10px] font-semibold tracking-wider">FT</span>

          {/* Crowd-cheer speaker — appears next to the FT label after the goal
              moment (no autoplay; click required). Hidden when admin disables
              `cheer_sound_enabled` in the CMS. */}
          {cheerEnabled && (
            <button
              type="button"
              onClick={() => playCrowdCheer(cheerSoundUrl)}
              title="Play crowd cheer"
              aria-label="Play crowd cheer"
              data-testid="success-tracker-cheer-btn"
              className={`absolute -top-1 translate-x-3 rounded-full p-1.5 transition-all hover:scale-110 active:scale-95 animate-psCheerButtonPop ${
                isLight
                  ? 'bg-white text-[#007a33] shadow-[0_2px_6px_rgba(0,122,51,0.25)] hover:bg-emerald-50'
                  : 'bg-emerald-900/60 text-emerald-200 ring-1 ring-emerald-400/30 hover:bg-emerald-800/70'
              }`}
              style={{ left: `calc(18px + (100% - 36px) * 1)` }}
            >
              <Volume2 className="w-3 h-3" strokeWidth={2.5} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
